import { Injectable, inject } from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {UcmimTranslationService} from '../translation.service';
import {VehicleListView} from './VehicleListView';
import { HttpClient } from '@angular/common/http';
import {CarData} from '@ucb2b/cardata-wizard/typings/cardata';
import {flatMap, map, tap} from 'rxjs/operators';
import {UpdateVehicleStatus} from './UpdateVehicleStatus';
import {Status} from './Status';
import {TdsTranslationData} from '../TdsTranslationData';
import {SelectItem} from '../vehicle-detail/SelectItem';
import {AppKeyToTranslationKeyPipe} from '../shared/pipes/app-key-to-translation-key.pipe';
import {VehicleStatusReason} from './VehicleStatusReason';
import {ExportFormat, ListPrint} from './vehicle-list-print';

/**
 * Vehicle list server response.
 */
interface VehicleListServerResponse {
  /**
   * Model text.
   * E.g.: Porsche Cayman GT4 981.
   */
  model: string;
  /**
   * Initial registration date.
   */
  initialRegistrationDate: Date;
  /**
   * Motorcapacity.
   */
  motorCapacity: number;
  /**
   * Power in kw.
   */
  powerKw: number;
  /**
   * Power in kw.
   */
  powerPs: number;
  /**
   * Fuel codes.
   */
  fuel: string;
  /**
   * Applications that have access to this vehicle.
   */
  applications: string[];
  /**
   * Mileage in km.
   */
  mileage: number;
  /**
   * CarHubId.
   */
  carHubId: string;
  /**
   * Inventory id.
   *
   * Combination of dealer number and vehicle number (dequence within dealer)
   * prefixed with country.
   *
   * E.g.: 'AT0100800024'.
   */
  inventoryId: string;

  vin: string;

  modelCode: string;

  standingDays: number;

  statusReason: VehicleStatusReason;
  failedValidations: [
    { clientKey: string; validationLevel: string; violatedCarHubField: string },
  ],
  vehicleId: string;
}

/**
 * CRUD for all views of vehicle.
 */
@Injectable({
  providedIn: 'root',
})
export class VehicleListService {
  private typeDataTranslationService = inject(UcmimTranslationService);
  private httpClient = inject(HttpClient);
  private appKeyToTranslationKeyPipe = inject(AppKeyToTranslationKeyPipe);

  archiveBool = new BehaviorSubject<boolean>(false);
  reactivateBool = new BehaviorSubject<boolean>(false);
  private vehicles = new BehaviorSubject<VehicleListView[]>([]);
  private deleteSearch = new Subject<void>();
  private selectedDealerId = new BehaviorSubject<string | undefined>(undefined);

  private static getTextIfPresent(selectItem?: SelectItem) {
    return selectItem ? selectItem.description ?? selectItem.fuelTextShort : '—';
  }

  private static getValueOrPlaceholder(
    value: any,
    unit?: any,
    placeholder: string = '—',
  ) {
    return value || Number.isFinite(value)
      ? unit
        ? value + unit
        : value
      : placeholder;
  }

  public setDealerId(dealerId: string) {
    this.selectedDealerId.next(dealerId);
  }

  /**
   * Get all vehicles of the current dealer.
   */
  getVehicles(status: Status): Observable<VehicleListView[]> {
    this.httpClient
      .get<VehicleListServerResponse[]>(`/api/vehicles?status=${status}`)
      .pipe(
        flatMap((response) =>
          this.typeDataTranslationService.getTdsDataWhenReady().pipe(
            map((typeData) =>
              response.map((unlocalizedVehicle) =>
                this.localizeVehicle(unlocalizedVehicle, typeData),
              ),
            ),
            tap((vehicles) => this.vehicles.next(vehicles)),
          ),
        ),
      )
      .subscribe((vehicles) => {
        // workaround for refresh bug when releasing vehicle to CarHub and switching back to list view (CarHub id wasn't there)
        this.vehicles.next([]);
        this.vehicles.next(vehicles);
      });
    return this.vehicles;
  }

  getActiveVehicleCount(): Observable<number> {
    return this.vehicles.pipe(map((vehicles) => vehicles.length));
  }

  /**
   * Send tds carData to server
   */
  saveCarData(carData: CarData): Observable<number> {
    let url = `/api/vehicles/saveVehicle`;
    if (this.selectedDealerId.value) {
      url += '?dealerId=' + this.selectedDealerId.value;
    }
    return this.httpClient.post<number>(url, carData);
  }

  /**
   * Send update vehicle status
   */
  updateVehicleStatus(vehicleStatus: UpdateVehicleStatus): Observable<void> {
    return this.httpClient
      .post<VehicleListServerResponse[]>('/api/vehicles/status', vehicleStatus)
      .pipe(
        flatMap((response) =>
          this.typeDataTranslationService
            .getTdsDataWhenReady()
            .pipe(
              map((typeData) =>
                response.map((unlocalizedVehicle) =>
                  this.localizeVehicle(unlocalizedVehicle, typeData),
                ),
              ),
            ),
        ),
        map((vehicle) => this.vehicles.next(vehicle)),
      );
  }

  updateDeleteSearchObservable() {
    this.deleteSearch.next();
    return this.deleteSearch.asObservable();
  }

  getApplicationName(application: string) {
    const appKeyTranslation = this.appKeyToTranslationKeyPipe.transform(application);
    return this.typeDataTranslationService.getSelectedKey('CAR_HUB_APPLICATION.' + appKeyTranslation);
  }

  public printVehicleList(printData: ListPrint, format: ExportFormat) {
    return this.httpClient.post(
      `/api/print/list/${format.extension}`,
      printData,
      {
        responseType: 'arraybuffer',
      },
    );
  }

  /**
   * Localize vehicle retrieved from server.
   */
  private localizeVehicle(
    unlocalizedVehicle: VehicleListServerResponse,
    typeData: TdsTranslationData,
  ): VehicleListView {
    return {
      applications: unlocalizedVehicle.applications,
      carHubId: unlocalizedVehicle.carHubId,
      fuel: VehicleListService.getTextIfPresent(
        typeData.fuels.find((f) => f.code === unlocalizedVehicle.fuel),
      ),
      initialRegistrationDate: unlocalizedVehicle.initialRegistrationDate,
      inventoryId: unlocalizedVehicle.inventoryId,
      mileage: unlocalizedVehicle.mileage,
      model: unlocalizedVehicle.model,
      motorCapacity: VehicleListService.getValueOrPlaceholder(
        Math.round(unlocalizedVehicle.motorCapacity),
        'ccm³',
      ),
      powerKw: unlocalizedVehicle.powerKw + 'kW',
      powerPs: unlocalizedVehicle.powerPs + 'PS',
      vin: unlocalizedVehicle.vin,
      modelCode: unlocalizedVehicle.modelCode,
      standingDays: unlocalizedVehicle.standingDays,
      statusReason: unlocalizedVehicle.statusReason,
      failedValidations: unlocalizedVehicle.failedValidations,
      vehicleId: unlocalizedVehicle.vehicleId,
    };
  }
}
